import { useEffect, useRef, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import Confetti from 'react-confetti';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import { useCheckout } from '../../../context';
import Button from 'components/Button';
import CenteredContainer from 'components/CenteredContainer';
import MembershipCard from 'components/MembershipCard';

import styles from './styles.module.scss';
import i18n from 'i18n';

const SuccessStep = () => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [numberOfPieces, setNumberOfPieces] = useState(100);
  const { organization, cart, user } = useCheckout();
  const router = useRouter();
  const confettiZoneRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(['organizations']);
  const updateConfettiDimensions = () => {
    if (confettiZoneRef.current) {
      setHeight(confettiZoneRef.current.clientHeight);
      setWidth(confettiZoneRef.current.clientWidth);

      const isMediumScreen = window.innerWidth >= 768;
      setNumberOfPieces(isMediumScreen ? 250 : 100);
    }
  };

  // Right now we only support one product per checkout session, so we can just get the first product
  const product =
    cart.selectedProducts[Object.keys(cart.selectedProducts)[0]]?.productData;

  useEffect(() => {
    updateConfettiDimensions();
    window.addEventListener('resize', updateConfettiDimensions);
    return () => {
      window.removeEventListener('resize', updateConfettiDimensions);
    };
  }, []);

  return (
    <div>
      <div
        className="absolute top-0 left-0 w-full h-[200px] md:h-[450px]"
        style={{
          backgroundImage: `url(${organization?.coverImageUrl})`,
          backgroundSize: 'cover'
        }}
      />
      <div
        className="absolute top-0 left-0 w-full h-[200px] md:h-[450px] backdrop-blur-[20px]"
        style={{
          background:
            'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(237, 237, 237, 0.4) 50.18%, #fff 100%)'
        }}
      />
      <div
        className="absolute top-0 left-0 w-full h-[200px] md:h-[450px] pointer-events-none"
        id="confetti-zone"
        ref={confettiZoneRef}
        style={{
          maskImage:
            'linear-gradient(to bottom, black 20%, rgba(0, 0, 0, 0.7) 50%, rgba(0, 0, 0, 0.3) 80%, transparent)',
          WebkitMaskImage:
            'linear-gradient(to bottom, black 20%, rgba(0, 0, 0, 0.7) 50%, rgba(0, 0, 0, 0.3) 80%, transparent)'
        }}
      >
        <Confetti
          width={width}
          height={height}
          numberOfPieces={numberOfPieces}
          colors={['#643DC4', '#F98700', '#7994f7']}
          drawShape={(ctx) => {
            ctx.beginPath();
            ctx.arc(0.2, 0.2, 4, 0, 2 * Math.PI);
            ctx.fill();

            ctx.closePath();
          }}
        />
      </div>
      <div
        className="h-full relative flex flex-col items-center justify-start pb-40 -mt-6 md:mt-20"
        data-testid="view-checkout-success"
      >
        <div className="w-full flex flex-col items-center md:p-10 pb-10 p-4">
          <div className={styles.scaleIn}>
            <div className="flex items-center justify-center">
              <div className={styles.eventPhoto}>
                <img
                  className="w-24 h-24 object-cover rounded-full shadow-xl"
                  src={organization.badgeImageUrl}
                  alt={organization.name}
                />
              </div>

              <div className={styles.checkMark}>
                <div className="w-24 h-24 object-cover rounded-full bg-primary-400 flex items-center justify-center shadow-xl">
                  <FaCheck size={40} color="white" />
                </div>
              </div>
            </div>
          </div>
          <h1 className="mt-10 text-2xl font-bold">
            {t('organizations:memberships.checkout.successTitle')}
          </h1>
          <p className="mt-4 text-lg text-gray-600 font-normal text-center max-w-[283px] md:max-w-none pb-6">
            {product.validFrom &&
              t('organizations:memberships.checkout.membershipAccessibleFrom', {
                startDate: new Date(product.validFrom).toLocaleDateString(
                  i18n.language,
                  {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  }
                )
              })}
            {!product.validFrom &&
              product.validUntil &&
              t('organizations:memberships.checkout.membershipAccessibleUntil')}
          </p>
          <MembershipCard
            orgImageUrl={organization.badgeImageUrl}
            orgName={organization.name}
            membershipName={product.product.name}
            startDate={product.validFrom}
            endDate={product.validUntil}
            holderName={user.name}
            holderImageUrl={user?.photo?.url}
            onClick={() => {
              router.push(`/memberships`);
            }}
          />
          <p className="mt-8 text-sm text-gray-500 font-normal text-center">
            {t('organizations:memberships.checkout.membershipCardAccess')}
          </p>
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white z-10 border-t border-[#EBEBEB]">
        <CenteredContainer>
          <Button
            variant="primaryDark"
            size="large"
            className="w-full mt-0!"
            onClick={() => {
              router.push(`/organizations/${organization.id}`);
            }}
          >
            {t('organizations:actions.view')}
          </Button>
        </CenteredContainer>
      </div>
    </div>
  );
};

export default SuccessStep;
